import { Helmet } from "react-helmet-async";
import Navbar from "../../components/navbar/navbar";
import Footer from "../../components/footer/footer";
import "./terms-and-conditions.css";

const TermsAndConditions = () => {
  const callOutFee = "$80+GST";

  return (
    <>
      <Helmet>
        <title>Terms and Conditions | Interior 4U</title>
        <meta
          name="description"
          content="Read the booking, quote, deposit, parking, payment and terms for Interior 4U."
        />
        <link rel="canonical" href="https://interior4u.com.au/terms-and-conditions" />
      </Helmet>

      <Navbar />

      <main id="main-content" className="terms-page">
        <header className="terms-hero">
          <div className="terms-container">
            <h1>Terms and Conditions</h1>
          </div>
        </header>

        <article className="terms-content">
          <p className="terms-updated">Last updated: 1 October 2026</p>
          <p>
            These terms apply to all services provided by Interior 4U
            (“we”, “us” or “our”). By accepting a quote or confirming a booking, the
            customer agrees to these terms.
          </p>

          <section>
            <h2>Bookings and contact details</h2>
            <p>
              To arrange a booking, we need your name, the job address and a mobile number. 
              We will contact you by text to confirm the scheduled date. We will also send a text reminder the day before the job. 
              Please reply to confirm and ensure someone can provide access at the agreed time.
            </p>
          </section>

          <section>
            <h2>Estimates and quotes</h2>
            <p>
              We will respond to service requests via text message or email with an estimate based on the
              information and photographs provided by the customer. A formal written quote is available
              upon request and remains valid for 30 days from the date of issuance.
            </p>
            <p>
              Prices are based on the scope of work and the information available when we prepare the quote. 
              A quote covers only the work and materials specifically listed in it. 
              Changes to the scope may require a revised quote.
            </p>
            <p>
              Should the customer request additional services, or if supplementary work and materials 
              are deemed necessary, we will outline the required adjustments and any associated costs 
              prior to proceeding. Approved additional works will be billed accordingly.
            </p>
          </section>

          <section>
            <h2>Inaccurate Information, Scope Discrepancies, and Wasted Travel</h2>
            <p>
              The customer is required to provide accurate information regarding the job specifications, 
              site layout, and scope of work. If service execution is prevented on-site due to inaccurate project details, 
              incorrect scope descriptions, or unsuitability of the work environment resulting in unutilised travel, 
              a fee of {callOutFee} will be charged to the customer, and the quotation will be subject to revision.
            </p>
          </section>

          <section>
            <h2>Customer-Supplied Materials and Parts</h2>
            <p>
              In the event that parts, fixtures, or materials are supplied by the customer, the customer assumes 
              responsibility for ensuring their accuracy, operational suitability, correct sizing, and structural compatibility. 
              If a job cannot be completed due to incorrect dimensions, defective components, or improper specification of 
              customer-provided items, a fee of {callOutFee} will apply for the additional travel. 
            </p>
          </section>

          <section>
            <h2>Existing Conditions, Utilities, and Concealed Problems</h2>
            <p>
              Quotations are based on visible conditions or disclosures provided at the time of assessment. 
              Concealed damage, structural deterioration, unsuitable substrates, hidden utility services, or unforeseen 
              site anomalies that could not reasonably be anticipated may impact operations and require supplementary work.
            </p>
            <p>
              If hazardous substances (including asbestos, toxic mould, or non-compliant wiring) are encountered on site, 
              operations will pause immediately for occupational health and safety compliance. Costs associated with containment, 
              statutory testing, or professional abatement remain the responsibility of the customer.
            </p>
            <p>
              If such conditions alter the operational scope, we will communicate the necessary changes and any 
              associated financial adjustments, obtaining client authorisation prior to continuing.
            </p>
          </section>

          <section>
            <h2>Site Inspections</h2>
            <p>
              For requested on-site physical inspections, an inspection fee of {callOutFee} applies. 
              Should the customer subsequently accept the formal quotation for the corresponding works, this fee will 
              be credited against the final invoice total.
            </p>
          </section>

          <section>
            <h2>Access and Missed Appointments</h2>
            <p>
              If the customer fails to respond to confirmation reminders and access cannot be granted 
              upon our arrival at the scheduled time, a call-out fee of {callOutFee} will apply.
            </p>
          </section>

          <section>
            <h2>Timeline Delays, Bulky Items, and Third-Party Freight</h2>
            <p>
              For extended or multi-phased projects, unforeseen site constraints or coordination adjustments with other trades 
              may influence the overall timeline. We accepts no liability for project schedule disruptions originating 
              outside of our direct operational control.
            </p>
            <p>
              Where bulky items or materials require delivery, logistical coordination is essential. If delays occur via 
              third-party couriers, freight handlers, or external suppliers that cause interruptions to the agreed project schedule, 
              we bear no liability for resulting timeline setbacks or secondary expenses.
            </p>
          </section>

          <section>
            <h2>Deposits</h2>
            <p>
              For service orders exceeding <strong>$1,000+GST</strong>, a <strong>30% deposit</strong> is 
              required to confirm and secure the booking.
            </p>
          </section>

          <section>
            <h2>Outdoor Work and Weather Conditions</h2>
            <p>
              External service delivery is contingent upon safe and favourable weather conditions. For bookings scheduled 
              more than 14 days in advance, we will contact the customer 7 days prior to review meteorological conditions 
              and confirm scheduling viability. If inclement weather compromises safety or structural integrity, alternative 
              arrangements will be made.
            </p>
          </section>

          <section>
            <h2>Parking</h2>
            <p>
              If paid parking is required to carry out your job, the parking cost will be added to your invoice. Where possible, 
              we will let you know about the expected parking cost before work begins.
            </p>
          </section>

          <section>
            <h2>Payment Terms</h2>
            <p>Approved payment methods include cash, electronic bank transfer, and credit/debit card.</p>
            <p>Payment due on invoice date.</p>
          </section>

          <section>
            <h2>Customer Access and Job Preparation</h2>
            <p>
             Please provide accurate information about the job and ensure the work area is accessible and ready for us to carry out the agreed work. 
             Let us know beforehand about access requirements or site conditions that may affect the job.
            </p>
          </section>

          <section className="terms-contact">
            <h2>Questions?</h2>
            <p>Please contact us for inquiries regarding these terms and conditions or upcoming bookings.</p>
            <a href="tel:0412279273">0412 279 273</a>
            <a href="mailto:info.interior4u@gmail.com">info.interior4u@gmail.com</a>
          </section>
        </article>
      </main>

      <Footer />
    </>
  );
};

export default TermsAndConditions;